import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { fetchUsersByStatus } from '../../utils/userApi';

function SalesModal({ isOpen, onClose, sale, onSubmit, loading }) {
  const [users, setUsers] = React.useState([]);
  const [usersLoading, setUsersLoading] = React.useState(false);
  
  const [formData, setFormData] = React.useState({
    saleDate: '',
    finalAmount: '',
    clientName: '',
    status: 'completed',
    productName: '',
    expenseAmount: '',
    paymentMethod: '',
    paymentDate: '',
    constructionDate: '',
    pointerUserId: '',
    closerUserId: '',
    notes: ''
  });

  React.useEffect(() => {
    const fetchUsers = async () => {
      if (!isOpen) return;
      
      setUsersLoading(true);
      try {
        const userList = await fetchUsersByStatus('active', 100);
        setUsers(userList);
      } catch (err) {
        console.error('ユーザー取得エラー:', err);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen]);

  React.useEffect(() => {
    if (sale) {
      setFormData({
        saleDate: sale.sale_date || '',
        finalAmount: sale.final_amount || '',
        clientName: sale.client_name || '',
        status: sale.status || 'completed',
        productName: sale.product_name || '',
        expenseAmount: sale.expense_amount || '',
        paymentMethod: sale.payment_method || '',
        paymentDate: sale.payment_date || '',
        constructionDate: sale.construction_date || '',
        pointerUserId: sale.pointer_user_id ? String(sale.pointer_user_id) : '',
        closerUserId: sale.closer_user_id ? String(sale.closer_user_id) : '',
        notes: sale.notes || ''
      });
    } else {
      setFormData({
        saleDate: new Date().toISOString().split('T')[0],
        finalAmount: '',
        clientName: '',
        status: 'completed',
        productName: '',
        expenseAmount: '',
        paymentMethod: '',
        paymentDate: '',
        constructionDate: '',
        pointerUserId: '',
        closerUserId: '',
        notes: ''
      });
    }
  }, [sale, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {sale ? '売上を編集' : '新規売上を登録'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                売上日 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="saleDate"
                value={formData.saleDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ステータス <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">保留中</option>
                <option value="completed">完了</option>
                <option value="cancelled">キャンセル</option>
                <option value="refunded">返金済み</option>
                <option value="deleted">削除済み</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              顧客名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              required
              placeholder="顧客名を入力"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              商品名
            </label>
            <input
              type="text"
              name="productName"
              value={formData.productName}
              onChange={handleChange}
              placeholder="商品名を入力"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                確定金額 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="finalAmount"
                value={formData.finalAmount}
                onChange={handleChange}
                required
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                経費金額
              </label>
              <input
                type="number"
                name="expenseAmount"
                value={formData.expenseAmount}
                onChange={handleChange}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                支払方法
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">選択してください</option>
                <option value="bank_transfer">銀行振込</option>
                <option value="cash">現金</option>
                <option value="credit_card">クレジットカード</option>
                <option value="other">その他</option>
              </select>
            </div>


          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                入金日
              </label>
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                工事日
              </label>
              <input
                type="date"
                name="constructionDate"
                value={formData.constructionDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ポインター担当者
              </label>
              <select
                name="pointerUserId"
                value={formData.pointerUserId}
                onChange={handleChange}
                disabled={usersLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">{usersLoading ? '読み込み中...' : '選択してください'}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                クローザー担当者
              </label>
              <select
                name="closerUserId"
                value={formData.closerUserId}
                onChange={handleChange}
                disabled={usersLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">{usersLoading ? '読み込み中...' : '選択してください'}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              備考
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="備考を入力"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {loading ? '保存中...' : (sale ? '更新する' : '登録する')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SalesModal;
